import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Globe2, Send, Loader2, Heart, MapPin, Sparkles, Handshake } from "lucide-react";
import { Header } from "@/components/Header";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  ["United Kingdom", "🇬🇧"], ["United States", "🇺🇸"], ["Canada", "🇨🇦"], ["Australia", "🇦🇺"],
  ["India", "🇮🇳"], ["United Arab Emirates", "🇦🇪"], ["Italy", "🇮🇹"], ["Germany", "🇩🇪"],
  ["France", "🇫🇷"], ["Japan", "🇯🇵"], ["China", "🇨🇳"], ["Singapore", "🇸🇬"], ["Malaysia", "🇲🇾"],
  ["Netherlands", "🇳🇱"], ["Switzerland", "🇨🇭"], ["Sweden", "🇸🇪"], ["Norway", "🇳🇴"], ["Russia", "🇷🇺"],
  ["Saudi Arabia", "🇸🇦"], ["Qatar", "🇶🇦"], ["Kuwait", "🇰🇼"], ["New Zealand", "🇳🇿"], ["South Korea", "🇰🇷"],
  ["Spain", "🇪🇸"], ["Ireland", "🇮🇪"], ["Maldives", "🇲🇻"], ["Nepal", "🇳🇵"], ["Pakistan", "🇵🇰"],
  ["Bangladesh", "🇧🇩"], ["South Africa", "🇿🇦"], ["Brazil", "🇧🇷"], ["Other", "🌍"],
];

const CATS = {
  memory: { chip: "MEMORY", color: "#C77D2E", bg: "var(--brass-light)", icon: MapPin },
  wish: { chip: "WISH", color: "#7E22CE", bg: "rgba(126,34,206,0.12)", icon: Sparkles },
  friendship: { chip: "FRIENDSHIP", color: "#16a34a", bg: "rgba(22,163,74,0.12)", icon: Handshake },
};

const WishCard = ({ w }) => {
  const c = CATS[w.category] || CATS.friendship;
  return (
    <div data-testid={`wish-card-${w.id}`} className="rounded-3xl p-5 break-inside-avoid" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-2xl">{w.flag || "🌍"}</span>
        <span className="inline-flex items-center gap-1.5 font-ui text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: c.bg, color: c.color }}><c.icon size={12} /> {c.chip}</span>
        {w.visited && <span className="font-ui text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "var(--brass-light)", color: "var(--accent)" }}>Visited Sri Lanka 🇱🇰</span>}
      </div>
      <p className="font-ui text-sm mt-3 leading-relaxed whitespace-pre-line" style={{ color: "var(--text-2)" }}>“{w.message}”</p>
      <p className="font-ui text-xs mt-3" style={{ color: "var(--text-muted)" }}>— {w.name}{w.country ? `, ${w.country}` : ""}</p>
    </div>
  );
};

export default function WishWall() {
  const [wishes, setWishes] = useState([]);
  const [stats, setStats] = useState({ count: 0, countries: 0 });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", country: "", flag: "", category: "friendship", visited: false, message: "" });

  const load = () => {
    axios.get(`${API}/wishes`).then((r) => setWishes(r.data || [])).catch(() => {});
    axios.get(`${API}/wishes/stats`).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickCountry = (name) => {
    const found = COUNTRIES.find((c) => c[0] === name);
    setForm((f) => ({ ...f, country: name, flag: found ? found[1] : "🌍" }));
  };

  const submit = async () => {
    if (form.message.trim().length < 5) { toast.error("Please write a little message."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/wishes`, form);
      toast.success(data.message || "Thank you 🌏");
      setForm({ name: "", country: "", flag: "", category: "friendship", visited: false, message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally { setBusy(false); }
  };

  const shown = filter === "all" ? wishes : wishes.filter((w) => w.category === filter);

  return (
    <div className="min-h-screen pb-24" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/rooms/sindu-kamare" data-testid="friends-back" className="inline-flex items-center gap-2 font-ui text-sm font-bold mb-6" style={{ color: "var(--text-2)" }}>
          <ArrowLeft size={16} /> Back
        </Link>

        <div className="text-center mb-6">
          <span className="grid place-items-center w-16 h-16 rounded-full mx-auto" style={{ background: "var(--brass-light)", color: "var(--accent)" }}><Globe2 size={30} /></span>
          <h1 className="font-en text-4xl sm:text-5xl font-extrabold mt-4" style={{ color: "var(--text)" }}>Friends of Sri Lanka</h1>
          <p className="font-ui text-base mt-3 max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--text-2)" }}>
            A wall of friendship for the whole world. Whether you have visited our island, dream of visiting one day, or simply wish us well — leave a <b style={{ color: "var(--text)" }}>memory or a wish</b> for the people of Sri Lanka.
          </p>
          <p className="font-ui text-sm mt-3 italic max-w-xl mx-auto" style={{ color: "var(--text-muted)" }}>
            This is a place of friendship. Let us set aside past grievances, and together face the future a new way — so we may all see a kinder world. 🕊️
          </p>
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-8 my-8" data-testid="friends-stats">
          <div className="text-center">
            <p className="font-en text-4xl font-extrabold" style={{ color: "var(--accent)" }} data-testid="friends-count">{stats.count}</p>
            <p className="font-ui text-xs" style={{ color: "var(--text-muted)" }}>messages of goodwill</p>
          </div>
          <div className="text-center">
            <p className="font-en text-4xl font-extrabold" style={{ color: "var(--accent)" }} data-testid="friends-countries">{stats.countries}</p>
            <p className="font-ui text-xs" style={{ color: "var(--text-muted)" }}>countries reached 🌏</p>
          </div>
        </div>

        {!open && (
          <div className="text-center mb-8">
            <button onClick={() => setOpen(true)} data-testid="friends-write-btn" className="inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full font-ui font-bold text-base transition-transform active:scale-95 hover:scale-[1.02]" style={{ background: "var(--accent)", color: "#fff" }}>
              <Heart size={18} /> Leave a message
            </button>
          </div>
        )}

        {open && (
          <div className="rounded-3xl p-5 sm:p-6 mb-10" style={{ background: "var(--card)", border: "2px solid var(--border)" }} data-testid="friends-composer">
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name (optional)" data-testid="friends-name" className="h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
              <select value={form.country} onChange={(e) => pickCountry(e.target.value)} data-testid="friends-country" className="h-12 px-3 rounded-xl font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }}>
                <option value="">Your country</option>
                {COUNTRIES.map(([n, f]) => <option key={n} value={n}>{f} {n}</option>)}
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="friends-category" className="h-12 px-3 rounded-xl font-ui text-sm outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }}>
                <option value="friendship">A message of friendship</option>
                <option value="memory">A memory of Sri Lanka</option>
                <option value="wish">A wish for Sri Lanka</option>
              </select>
              <label className="flex items-center gap-2 h-12 px-4 rounded-xl font-ui text-sm cursor-pointer" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text-2)" }}>
                <input type="checkbox" checked={form.visited} onChange={(e) => set("visited", e.target.checked)} data-testid="friends-visited" /> I have visited Sri Lanka 🇱🇰
              </label>
            </div>
            <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Write your memory or wish for the people of Sri Lanka…" rows={4} data-testid="friends-message" className="w-full mt-3 p-4 rounded-xl font-ui outline-none resize-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }} />
            <div className="flex gap-2 mt-3">
              <button onClick={submit} disabled={busy} data-testid="friends-submit" className="flex-1 h-12 rounded-xl font-ui font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
                {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} Send with love
              </button>
              <button onClick={() => setOpen(false)} className="h-12 px-5 rounded-xl font-ui font-bold" style={{ background: "var(--bg)", color: "var(--text)", border: "2px solid var(--border)" }}>Cancel</button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-center mb-6" data-testid="friends-filters">
          {[["all", "All"], ["friendship", "Friendship"], ["memory", "Memories"], ["wish", "Wishes"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`friends-filter-${k}`} className="h-9 px-4 rounded-full font-ui font-bold text-xs" style={{ background: filter === k ? "var(--accent)" : "var(--card)", color: filter === k ? "#fff" : "var(--text-2)", border: "1px solid var(--border)" }}>{l}</button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="text-center font-ui text-sm py-10" style={{ color: "var(--text-muted)" }} data-testid="friends-empty">
            No messages yet. Be the first friend to send love to Sri Lanka. 🕊️
          </p>
        ) : (
          <div className="columns-1 sm:columns-2 gap-4 space-y-4" data-testid="friends-wall">
            {shown.map((w) => <WishCard key={w.id} w={w} />)}
          </div>
        )}
      </div>
    </div>
  );
}
